'use client';

import { SxProps, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ReactNode, useId } from 'react';

import { StrapiImage } from '@/components/Utility/StrapiImage';
import { Review } from '@/types';

export const ReviewBox = ({
  children,
  classNames = '',
  sx = {},
}: {
  children: ReactNode;
  classNames?: string;
  sx?: SxProps;
}) => {
  return (
    <Box
      className={classNames}
      data-component="ReviewBox"
      sx={{
        position: 'relative',
        textAlign: 'center',
        mx: 'auto',
        py: 6,
        px: 2,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

function getInitials(name?: string | null) {
  if (!name) return '';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

export function SingleReview({
  review,
  classNames = '',
  sx = {},
}: {
  review: Review;
  classNames?: string;
  sx?: SxProps;
}) {
  const hasStrapiAvatar = Boolean(review.Avatar);

  return (
    <ReviewBox classNames={classNames} sx={sx}>
      {/* Avatar */}
      <Box
        sx={(theme) => ({
          background: theme.palette.common.white,
          width: '8.6rem',
          height: '8.6rem',
          position: 'absolute',
          top: '-5.6rem',
          left: 'calc(50% - 4.3rem)',
          borderRadius: '4.3rem',
          boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
          img: {
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
            position: 'absolute',
            borderRadius: '4rem',
            objectFit: 'cover',
            width: '8rem',
            height: '8rem',
          },
        })}
      >
        {hasStrapiAvatar ? (
          <StrapiImage image={review.Avatar!} size="thumbnail" imageWidth={128} imageHeight={128} />
        ) : (
          <Box
            aria-label={review.Author}
            role="img"
            sx={(theme) => ({
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%,-50%)',
              width: '8rem',
              height: '8rem',
              borderRadius: '4rem',
              display: 'grid',
              placeItems: 'center',
              background: theme.palette.gradients.gradient1,
              color: theme.palette.common.white,
              fontSize: '2.5rem',
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: 0,
            })}
          >
            {getInitials(review.Author)}
          </Box>
        )}
      </Box>

      {/* Stars */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.4,
          mb: 1.2,
          mt: 3,
        }}
      >
        {[...Array(Math.min(review?.Rating ?? 0, 5))].map((_, i) => (
          <Star key={`${review.documentId}_star_${i}`} />
        ))}
      </Box>

      {/* Author */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontWeight: 600, mt: 1 }}>
          {review.Author}
        </Typography>
        <Typography
          variant="medium"
          sx={{
            display: 'block',
            marginBottom: 4,
          }}
        >
          {[review.Role, review.CompanyName].filter(Boolean).join(', ')}
        </Typography>
      </Box>

      {/* Review Text */}
      <Typography
        sx={{
          lineHeight: 1.5,
        }}
        variant="medium"
      >
        “{review.ReviewText}”
      </Typography>
    </ReviewBox>
  );
}

export function Star({ id }: { id?: string }) {
  const theme = useTheme();
  // Avoid hydration mismatches: Math.random() will differ server vs client.
  // useId() is stable across SSR + hydration.
  const reactId = useId();
  const gradientId = id || `star-gradient-${reactId}`;
  return (
    <svg width="24" height="24" viewBox="0 0 27 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.5 0L18.207 7.52139L26.8148 9.67376L21.1161 16.4746L21.729 25.3262L13.5 22.008L5.27101 25.3262L5.88394 16.4746L0.185208 9.67376L8.79302 7.52139L13.5 0Z"
        fill={`url(#${gradientId})`}
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="-0.5"
          y1="14"
          x2="27.5"
          y2="14"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={theme.palette.brand.primarySecondary1} />
          <stop offset="1" stopColor={theme.palette.brand.secondary1} />
        </linearGradient>
      </defs>
    </svg>
  );
}
